/**
 * JAISIU-3558 — streaming transcript tail helpers.
 * In-progress assistant text lives inside `chatMessages` until `chat.final`.
 */
export type StreamingTranscriptTail = {
  id: string;
  role: "assistant";
  content: string;
  timestamp: number;
  streaming: true;
  runId: string;
  reasoning?: string;
};

export function isStreamingTranscriptTail(message: unknown): message is StreamingTranscriptTail {
  if (!message || typeof message !== "object") {
    return false;
  }
  const record = message as { streaming?: unknown; role?: unknown };
  return record.streaming === true && record.role === "assistant";
}

function tailRunId(message: unknown): string | undefined {
  if (!isStreamingTranscriptTail(message)) {
    return undefined;
  }
  const record = message as { runId?: unknown };
  return typeof record.runId === "string" ? record.runId.trim() || undefined : undefined;
}

export function findStreamingTailIndex(
  messages: readonly unknown[],
  runId: string | null | undefined,
): number {
  if (!runId || typeof runId !== "string") {
    return -1;
  }
  const trimmed = runId.trim();
  if (!trimmed) {
    return -1;
  }
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    const id = tailRunId(message);
    if (id === trimmed) {
      return i;
    }
  }
  return -1;
}

export function extendStreamingTail(
  messages: readonly unknown[],
  args: {
    runId: string;
    text: string;
    reasoning?: string;
    timestamp: number;
  },
): unknown[] {
  if (!args || typeof args !== "object") {
    return messages as unknown[];
  }
  const trimmedRunId = args.runId.trim();
  if (!trimmedRunId) {
    return messages as unknown[];
  }
  const existingIndex = findStreamingTailIndex(messages, trimmedRunId);
  if (existingIndex === -1) {
    const next: StreamingTranscriptTail = {
      id: trimmedRunId,
      role: "assistant",
      content: args.text,
      timestamp: args.timestamp,
      streaming: true,
      runId: trimmedRunId,
      ...(typeof args.reasoning === "string" ? { reasoning: args.reasoning } : {}),
    };
    return [...messages, next];
  }
  const existing = messages[existingIndex] as StreamingTranscriptTail;
  const nextReasoning = typeof args.reasoning === "string" ? args.reasoning : existing.reasoning;
  const updated: StreamingTranscriptTail = {
    ...existing,
    content: args.text,
    timestamp: args.timestamp,
    ...(nextReasoning !== undefined ? { reasoning: nextReasoning } : {}),
  };
  if (
    updated.content === existing.content &&
    updated.timestamp === existing.timestamp &&
    updated.reasoning === existing.reasoning
  ) {
    return messages as unknown[];
  }
  const next = messages.slice();
  next[existingIndex] = updated;
  return next;
}

export function seedStreamingTailFromLiveFields(
  messages: readonly unknown[],
  args: {
    runId: string | null;
    stream: string | null;
    reasoningStream: string | null;
    streamStartedAt: number | null;
  },
): unknown[] {
  const runId = typeof args.runId === "string" ? args.runId.trim() : "";
  if (!runId) {
    return messages as unknown[];
  }
  const text = typeof args.stream === "string" ? args.stream : "";
  const reasoning = typeof args.reasoningStream === "string" ? args.reasoningStream : "";
  if (!text && !reasoning.trim()) {
    return messages as unknown[];
  }
  return extendStreamingTail(messages, {
    runId,
    text,
    ...(reasoning.trim() ? { reasoning } : {}),
    timestamp: args.streamStartedAt ?? Date.now(),
  });
}

export function dropStreamingTail(
  messages: readonly unknown[],
  runId: string | null | undefined,
): unknown[] {
  if (!runId || typeof runId !== "string") {
    return messages as unknown[];
  }
  const trimmed = runId.trim();
  if (!trimmed) {
    return messages as unknown[];
  }
  const index = findStreamingTailIndex(messages, trimmed);
  if (index === -1) {
    return messages as unknown[];
  }
  const next = messages.slice();
  next.splice(index, 1);
  return next;
}
